/**
 * The game session: owns the world, the scene, the network connection, and
 * the frame loop. Everything else is a subsystem it drives.
 */

import * as THREE from 'three';
import {
  World,
  ATMOSPHERE,
  CAMERA,
  CLIENT_SEND_RATE,
  CONFRONT,
  GEAR,
  ITEMS,
  LifeState,
  MatchPhase,
  MONSTER,
  MonsterState as Fsm,
  PLAYER,
  Stance,
  TOWERS,
  ClientMsg,
  CueKind,
  angleDelta,
  clamp,
  clamp01,
  forwardFromYaw,
  yawToward,
  isPassiveItem,
  hidingCapacity,
  profileFor,
  damp,
  dist2d,
  type GameState,
  type MoveMsg,
  type PlayerState,
} from '@static-pines/shared';

import { SceneRenderer } from '../render/Scene.js';
import { buildTerrain } from '../render/Terrain.js';
import { Forest } from '../render/Forest.js';
import { Props } from '../render/Props.js';
import { MonsterModel } from '../render/MonsterModel.js';
import { RemotePlayers, type RemoteState } from '../render/RemotePlayers.js';
import { HELD_TORCH, TORCH_AIM_DISTANCE, makeTorchModel } from '../render/Torch.js';
import {
  makeSurvivorFigure,
  poseSurvivorFigure,
  type SurvivorFigure,
} from '../render/Survivor.js';
import { AudioEngine } from '../audio/AudioEngine.js';
import { Connection } from '../net/Connection.js';
import { VoiceChat } from '../net/VoiceChat.js';
import { Hud, TransmitPanel, type HudModel } from '../ui/Hud.js';
import type { MapModel } from '../ui/MapPanel.js';
import { Input, IS_TOUCH_DEVICE } from './Input.js';
import { TouchControls } from './TouchControls.js';
import { LocalPlayer } from './LocalPlayer.js';

/** Beyond this a footfall is heard but not felt. */
const IMPACT_RANGE = 55;

/**
 * How long a locally-predicted flashlight toggle or hiding attempt gets
 * before the server's word overrules it.
 *
 * Both are optimistic: the client acts before the round trip confirms it, so
 * they can be wrong — a toggle sent while dying is silently dropped server
 * side, a battery hitting zero force-turns the torch off with nothing told
 * to the owning client, and a hiding spot someone else just took is refused
 * with no correction message at all. Rather than special-case each cause,
 * any disagreement that outlives one round trip is resolved in the server's
 * favour.
 */
const PREDICTION_TIMEOUT_MS = 400;

/**
 * How far in front of the listener an open-channel voice is placed.
 *
 * Comfortably inside VOICE.REF_DISTANCE, so the panner's distance curve is
 * flat and everyone is heard at full volume — but not *at* the listener, which
 * would leave the HRTF with a zero-length direction to work from. The effect
 * is a conference call rather than positional audio: once the run is over,
 * proximity has stopped being the point.
 */
const OPEN_CHANNEL_DISTANCE = 1;

/** Spectator orbit: how far the camera sits from whoever it is following. */
const SPECTATE_DISTANCE = 4.6;
/** Elevation limits, so the orbit never tips over the top or under the floor. */
const SPECTATE_PITCH_MIN = -0.5;
const SPECTATE_PITCH_MAX = 1.25;

/** What the player is currently standing next to, if anything. */
interface Interactable {
  kind: 'item' | 'tower' | 'hiding' | 'ladder' | 'revive';
  id: number;
  label: string;
  distance: number;
  /** Shown, but E does nothing — a full hiding place still needs a label. */
  disabled?: boolean;
  /** Revive only: whose body this is. See InteractMsg.sessionId. */
  sessionId?: string;
}

export class Game {
  readonly renderer: SceneRenderer;
  readonly input: Input;

  private world!: World;
  private player!: LocalPlayer;
  private forest!: Forest;
  private props!: Props;
  private monster = new MonsterModel();
  private remotes = new RemotePlayers();

  private readonly audio: AudioEngine;
  private readonly connection: Connection;
  private readonly hud: Hud;
  private readonly transmit: TransmitPanel;
  /** Proximity voice. Public so main.ts can route relayed signals into it. */
  readonly voice: VoiceChat;
  /** On-screen controls; null on devices with a fine pointer. */
  private touch: TouchControls | null = null;
  /**
   * Whether the map panel is open on touch devices, where it is a toggle
   * rather than a passive: the corner it occupies is the button row there.
   * Starts closed — on a phone you take the map out when you need it.
   */
  private touchMapOpen = false;

  private flashlight!: THREE.SpotLight;
  /**
   * Your own rig, mirroring the one every teammate's avatar has: the anchor
   * carries position and heading, the pivot carries pitch, and the body and
   * the torch both ride inside it. See buildWorld.
   */
  private selfAnchor!: THREE.Group;
  private torchPivot!: THREE.Group;
  private torchVisuals!: THREE.Group;
  /** Your body, drawn only once the view has pulled back far enough to see it. */
  private selfFigure!: SurvivorFigure;
  private flashlightOn = false;
  /** performance.now() of the last local flashlight prediction; see syncFlashlight. */
  private flashlightPredictedAt = 0;
  /** performance.now() of the last local hiding prediction; see syncHiding. */
  private hidingPredictedAt = 0;
  /** Last observed life state, to catch the frame you are revived on. */
  private lastLife: number = LifeState.Alive;

  private running = false;
  private lastFrame = performance.now();
  private footstepAccumulator = 0;
  private dread = 0;
  private deathFade = 0;
  private spectateTarget: string | null = null;
  /** Spectator orbit angles around the subject. See updateSpectate. */
  private spectateYaw = 0;
  private spectatePitch = 0.3;
  private currentInteractable: Interactable | null = null;
  private flareObjects = new Map<
    string,
    { light: THREE.PointLight; mesh: THREE.Mesh; glow: THREE.Mesh }
  >();
  /**
   * Towers this player has personally been near.
   *
   * Client-side and cosmetic: the map is a memory aid, not authoritative
   * state, so there is no reason to spend bandwidth syncing it. Activated
   * towers are known to everyone regardless, since those come from game state.
   */
  private readonly discoveredTowers = new Set<number>();
  private radioTimer = 0;
  /** Which hotbar slot is selected, by index rather than by item kind. */
  private selectedIndex = 0;
  private lastServerHeld = '';
  private baseFov = 72;
  /** Screen-shake accumulator, 0..1. */
  private trauma = 0;
  private shakeTime = 0;
  /** Set while a forced confrontation is running, so it stays visible. */
  private confrontFocus = false;
  /** Flashlight state to put back after a confrontation forces it on. */
  private torchRestore: boolean | null = null;

  constructor(
    private readonly container: HTMLElement,
    audio: AudioEngine,
    connection: Connection,
    hud: Hud,
    transmit: TransmitPanel,
  ) {
    this.audio = audio;
    this.connection = connection;
    this.hud = hud;
    this.transmit = transmit;
    this.voice = new VoiceChat(audio, connection);

    this.renderer = new SceneRenderer(container);
    this.baseFov = this.renderer.camera.fov;
    this.input = new Input(this.renderer.domElement);

    this.bindKeys();

    if (IS_TOUCH_DEVICE) {
      this.touch = new TouchControls(container, this.input);

      // The MAP button presses this synthetic code, as if the map had a key.
      this.input.on('MapToggle', () => {
        this.touchMapOpen = !this.touchMapOpen;
      });

      // The hotbar becomes its own input surface: tap to select AND use,
      // hold to drop. There is no separate use button — the slot is the
      // button, which spares a thumb-sized circle of screen.
      this.hud.onSlotTap = (index) => this.selectSlot(index);
      this.hud.onSlotUse = (index) => {
        this.selectSlot(index);
        this.useSelected();
      };
      this.hud.onSlotHold = (index) => {
        this.selectSlot(index);
        this.connection.send(ClientMsg.DropItem);
      };
    }
  }

  // -------------------------------------------------------------------------
  // Setup
  // -------------------------------------------------------------------------

  /**
   * Build the world. Called once the server has sent Welcome.
   *
   * Difficulty is required, not optional: it changes tower, item and hiding
   * counts, so building with the wrong value produces a different forest from
   * everyone else's.
   */
  buildWorld(seed: number, difficulty: number): void {
    this.world = new World(seed, difficulty);

    this.renderer.scene.add(buildTerrain(this.world));

    this.forest = new Forest(this.world);
    this.renderer.scene.add(this.forest.group);

    this.props = new Props(this.world);
    this.renderer.scene.add(this.props.group);

    this.renderer.scene.add(this.monster.group);
    this.renderer.scene.add(this.remotes.group);

    this.player = new LocalPlayer(this.world, this.world.spawnPoint);

    this.flashlight = new THREE.SpotLight(
      0xffeecc,
      ATMOSPHERE.FLASHLIGHT_INTENSITY,
      ATMOSPHERE.FLASHLIGHT_RANGE,
      ATMOSPHERE.FLASHLIGHT_ANGLE,
      ATMOSPHERE.FLASHLIGHT_PENUMBRA,
      ATMOSPHERE.FLASHLIGHT_DECAY,
    );
    this.flashlight.castShadow = true;
    this.flashlight.shadow.mapSize.set(1024, 1024);
    this.flashlight.shadow.camera.near = 0.5;
    this.flashlight.shadow.camera.far = ATMOSPHERE.FLASHLIGHT_RANGE;
    this.flashlight.shadow.bias = -0.0016;
    this.flashlight.visible = false;

    /**
     * Your torch is an object in the world, not a lamp bolted to your eyes.
     *
     * It used to hang off the camera, which is fine while the camera *is* your
     * head — but the moment the view pulls back for hiding or a ladder, the
     * beam left from three metres behind you, lighting your own back. Patching
     * that with an offset works only for as long as every future camera move
     * remembers to patch it too.
     *
     * So the rig mirrors the one every teammate already has: an anchor at your
     * feet carrying your heading, a pivot at the torch's mounting point
     * carrying your pitch, and the light and casing riding inside it. Where
     * the camera happens to be stops mattering entirely.
     */
    this.selfAnchor = new THREE.Group();
    this.torchPivot = new THREE.Group();
    this.torchVisuals = makeTorchModel();

    this.torchPivot.add(this.torchVisuals);
    this.torchPivot.add(this.flashlight);
    this.torchPivot.add(this.flashlight.target);
    this.flashlight.position.set(0, 0, 0);
    this.flashlight.target.position.set(0, 0, -TORCH_AIM_DISTANCE);

    this.selfAnchor.add(this.torchPivot);

    /**
     * Your own body, on the same anchor.
     *
     * First person never needed one, and there was none — which stopped being
     * fine the moment hiding places and ladders pulled the view off your
     * shoulder. A ladder being climbed by nobody, with a torch floating up it
     * on its own, reads as a bug and loses the one thing the pulled-back view
     * was for: seeing where you actually are on the rungs.
     *
     * Parented rather than positioned separately, so the body and the torch
     * cannot disagree about where you are standing or which way you face.
     */
    this.selfFigure = makeSurvivorFigure();
    this.selfFigure.group.visible = false;
    this.selfAnchor.add(this.selfFigure.group);

    this.renderer.scene.add(this.selfAnchor);
  }

  private bindKeys(): void {
    // The flashlight is not an inventory item. It has its own key and its own
    // battery, and is always available regardless of what is in the hotbar.
    this.input.on('KeyF', () => this.toggleFlashlight());
    this.input.on('KeyR', () => this.toggleRadio());
    this.input.on('KeyV', () => {
      if (this.voice.status !== 'live') return;
      const muted = this.voice.toggleMute();
      this.hud.showNotice(muted ? 'Microphone muted.' : 'Microphone live. He can hear you.', 2);
    });
    this.input.on('KeyE', () => this.interact());
    this.input.on('KeyQ', () => this.connection.send(ClientMsg.DropItem));

    for (let slot = 1; slot <= 6; slot++) {
      this.input.on(`Digit${slot}`, () => this.selectSlot(slot - 1));
    }

    this.input.onScroll((direction) => {
      // While down, the wheel walks the roster instead of the hotbar.
      if (this.isSpectating) this.cycleSpectate(direction);
      else this.cycleSlot(direction);
    });

    this.input.onClick((button) => {
      // Left steps forward through the living, right steps back.
      if (this.isSpectating) {
        this.cycleSpectate(button === 2 ? -1 : 1);
        return;
      }
      if (button === 0) this.useSelected();
    });

    this.input.on('Space', () => {
      // Space steps off a ladder, holds your breath while hiding, and switches
      // subject while down. Each context is mutually exclusive, so one key
      // serves all three without ambiguity — and it does nothing otherwise,
      // since there is no jump.
      if (this.isSpectating) {
        // A keyboard route to the same cycling the mouse does, for anyone who
        // would rather not click while watching.
        this.cycleSpectate(1);
      } else if (this.player?.isClimbing) {
        this.player.dismount();
      } else if (this.player?.hidingSpotId >= 0) {
        this.connection.send(ClientMsg.HoldBreath, true);
      }
    });

  }

  private toggleFlashlight(): void {
    if (!this.player || this.state?.phase === MatchPhase.Lobby) return;
    const me = this.me;
    // A dead or spectating player has no living counterpart server-side to
    // toggle: the server would silently drop the message anyway, so pressing
    // F here would only flip the local prediction with nothing to reconcile
    // it against until the next life-state change.
    if (!me || me.life !== LifeState.Alive) return;
    // The server refuses this outright while you are hidden, so say why
    // rather than letting the key appear to be broken.
    if (this.player.hidingSpotId >= 0) {
      this.hud.showNotice('Not from in here.', 2);
      return;
    }
    if (!this.flashlightOn && me.battery <= 0) {
      this.hud.showNotice('The battery is dead.', 2);
      return;
    }

    this.flashlightOn = !this.flashlightOn;
    this.flashlight.visible = this.flashlightOn;
    this.flashlightPredictedAt = performance.now();
    this.audio.click();
    this.connection.send(ClientMsg.ToggleFlashlight);
  }

  /**
   * Reconcile the locally-predicted torch state against the server's.
   *
   * See PREDICTION_TIMEOUT_MS for why this exists instead of trusting the
   * optimistic toggle unconditionally.
   */
  private syncFlashlight(me: PlayerState): void {
    if (
      me.flashlightOn !== this.flashlightOn &&
      performance.now() - this.flashlightPredictedAt >= PREDICTION_TIMEOUT_MS
    ) {
      this.flashlightOn = me.flashlightOn;
    }

    /**
     * The beam hangs off the camera, and on death the camera becomes a
     * free-roaming spectator rig — so a torch that was lit when you went down
     * would light the forest from wherever you happened to be watching from.
     *
     * Only the drawing stops. The state is deliberately left alone: your body
     * keeps its light where it fell, and the battery goes on draining, so
     * dying with the torch on still costs you the cell.
     */
    this.flashlight.visible = this.flashlightOn && me.life === LifeState.Alive;
  }

  /**
   * Catch the moment you are brought round.
   *
   * A downed client stops reporting its position, so its LocalPlayer is still
   * standing where it fell while the server has already moved the body over to
   * whoever revived it. Without adopting the server's position the first move
   * report reads as a teleport and comes straight back as a correction.
   */
  private syncLife(me: PlayerState): void {
    const revived = this.lastLife !== LifeState.Alive && me.life === LifeState.Alive;
    this.lastLife = me.life;
    if (!revived || !this.player) return;

    this.player.teleport(me.x, me.y, me.z);
    this.spectateTarget = null;
  }

  /**
   * Carry your body and your torch along with you.
   *
   * Position and heading go on the anchor, pitch on the pivot — the same split
   * a teammate's avatar uses, so your beam leaves your hand at the same place
   * and angle theirs does. Aiming the light is now just aiming the torch.
   */
  private updateSelfRig(): void {
    if (!this.player) return;

    this.selfAnchor.position.set(this.player.x, this.player.y, this.player.z);
    this.selfAnchor.rotation.y = this.player.yaw;

    // Rides the crouch with the hand holding it, tracking the eye height that
    // is already being damped rather than smoothing a second copy of it.
    this.torchPivot.position.set(
      HELD_TORCH.x,
      HELD_TORCH.y * this.player.eyeRatio,
      HELD_TORCH.z,
    );
    this.torchPivot.rotation.x = this.player.pitch;

    // Ducks with the view, exactly as a teammate's figure ducks with theirs.
    poseSurvivorFigure(this.selfFigure, this.player.eyeRatio);

    /**
     * The casing is drawn only once the view has pulled back far enough to see
     * it as an object rather than as a slab across the lens. The *light* is
     * untouched by this: it is on whenever the torch is on, from wherever the
     * torch is, which is the whole point of the rig.
     */
    const pulledBack = this.player.cameraOffset > CAMERA.SELF_DRAW_DISTANCE;
    this.torchVisuals.visible = this.flashlight.visible && pulledBack;

    /**
     * Your body, on the same threshold, with two extra conditions.
     *
     * Not while hidden: concealment is total, teammates see an empty bush, and
     * drawing a figure in there for its owner alone would be the one screen in
     * the game showing something nobody else can see. Not while down either —
     * the camera has become a spectator rig by then, and your standing body
     * would follow it around the forest while your actual corpse lies where it
     * fell.
     */
    const upright = !this.me || this.me.life === LifeState.Alive;
    this.selfFigure.group.visible = pulledBack && upright && this.player.hidingSpotId < 0;
  }

  /** Items that occupy a hotbar slot, in inventory order. */
  private activeItems(): string[] {
    const me = this.me;
    if (!me) return [];
    return [...me.inventory].filter((kind) => !isPassiveItem(kind));
  }

  /**
   * Selection is tracked by slot index, not by item kind.
   *
   * Keying off the kind broke with duplicates: `indexOf` always found the
   * first matching slot, so carrying two flares highlighted both and scrolling
   * down got stuck bouncing between them while scrolling up appeared to work.
   */
  /**
   * Switch the radio scanner on or off.
   *
   * Server-authoritative, because a live radio raises your noise floor and the
   * monster's perception has to see that. The client just asks.
   */
  private toggleRadio(): void {
    const me = this.me;
    if (!me || this.player?.isLookLocked) return;
    if (!me.inventory.includes('radio')) {
      this.hud.showNotice('You have no radio.', 2);
      return;
    }
    this.audio.click();
    this.connection.send(ClientMsg.ToggleRadio);
  }

  private selectSlot(index: number): void {
    if (this.player?.isLookLocked) return;
    const active = this.activeItems();
    if (index < 0 || index >= active.length) return;

    this.selectedIndex = index;
    this.pushSelection(active[index]!);
  }

  private cycleSlot(direction: number): void {
    if (this.player?.isLookLocked) return;
    const active = this.activeItems();
    if (active.length === 0) return;

    const count = active.length;
    this.selectedIndex = (((this.selectedIndex + direction) % count) + count) % count;
    this.pushSelection(active[this.selectedIndex]!);
  }

  private pushSelection(kind: string): void {
    this.lastServerHeld = kind;
    this.connection.send(ClientMsg.SelectItem, kind);
  }

  /**
   * Keep the local slot index and the server's held item agreeing.
   *
   * The server changes `heldItem` on its own when you pick something up or
   * consume the last of a stack, so those changes are adopted; changes the
   * player makes are pushed the other way.
   */
  private syncSelection(): void {
    const me = this.me;
    if (!me) return;

    const active = this.activeItems();
    if (active.length === 0) {
      this.selectedIndex = 0;
      this.lastServerHeld = me.heldItem;
      return;
    }

    if (me.heldItem !== this.lastServerHeld) {
      const index = active.indexOf(me.heldItem);
      if (index >= 0) this.selectedIndex = index;
      this.lastServerHeld = me.heldItem;
    }

    this.selectedIndex = Math.min(this.selectedIndex, active.length - 1);

    const want = active[this.selectedIndex]!;
    if (me.heldItem !== want) this.pushSelection(want);
  }

  /**
   * Left click. Only battery and flare have a use action — map and radio work
   * by being held, compass and booster by simply being carried.
   */
  private useSelected(): void {
    if (this.player?.isLookLocked || this.transmit.isVisible) return;

    const active = this.activeItems();
    const kind = active[this.selectedIndex];
    if (!kind) return;

    if (kind === 'battery' || kind === 'flare') {
      this.connection.send(ClientMsg.UseItem, { kind });
    }
  }

  // -------------------------------------------------------------------------
  // Interaction
  // -------------------------------------------------------------------------

  /**
   * E is the single "do the contextual thing" key, including cancelling.
   *
   * Escape is deliberately not used for any of this: the browser drops pointer
   * lock on Escape whatever we do, raising the pause veil, so an Escape
   * binding would always fire alongside a pause.
   */
  private interact(): void {
    if (!this.player || this.player.isLookLocked) return;

    if (this.hud.clueVisible) {
      this.hud.dismissClue();
      return;
    }

    if (this.transmit.isVisible) {
      this.stopTransmitting();
      return;
    }

    if (this.player.hidingSpotId >= 0) {
      this.exitHiding();
      return;
    }

    const target = this.currentInteractable;
    if (!target || target.disabled) return;

    switch (target.kind) {
      case 'item':
        this.connection.send(ClientMsg.Interact, { kind: 'item', id: target.id });
        break;

      case 'revive':
        this.connection.send(ClientMsg.Interact, {
          kind: 'revive',
          id: target.id,
          sessionId: target.sessionId,
        });
        break;

      case 'ladder':
        // Already on it — E must not re-mount, or it would cancel the climb.
        if (this.player.isClimbing) break;
        this.player.towerId = target.id;
        this.player.climbing = true;
        this.connection.send(ClientMsg.Interact, { kind: 'tower', id: target.id });
        break;

      case 'tower':
        this.startTransmitting(target.id);
        break;

      case 'hiding':
        this.player.enterHiding(target.id);
        this.hidingPredictedAt = performance.now();
        // The server puts the torch out on entry; predict it so the beam
        // does not linger for a round trip after you have ducked in. If the
        // spot turns out to be full, syncFlashlight lights it back up.
        if (this.flashlightOn) {
          this.flashlightOn = false;
          this.flashlight.visible = false;
          this.flashlightPredictedAt = performance.now();
        }
        this.connection.send(ClientMsg.EnterHiding, target.id);
        break;
    }
  }

  private startTransmitting(towerId: number): void {
    this.transmit.show();
    this.transmit.dial = 0.5;
    this.connection.send(ClientMsg.StartTransmit, towerId);
  }

  private stopTransmitting(): void {
    this.transmit.hide();
    this.connection.send(ClientMsg.StopTransmit);
  }

  private exitHiding(): void {
    if (!this.player) return;
    this.player.leaveHiding();
    this.hidingPredictedAt = performance.now();
    this.connection.send(ClientMsg.ExitHiding);
    this.connection.send(ClientMsg.HoldBreath, false);
  }

  /**
   * Reconcile the locally-predicted hiding pose against the server's.
   *
   * Entering a spot is optimistic — see interact() — so a request the server
   * refuses (the spot filled up as you pressed E) leaves the client posed as
   * hidden while the server still has it standing in the open, fully visible
   * and grabbable. The server says *why* it refused, so this only has to undo
   * the pose. See PREDICTION_TIMEOUT_MS.
   *
   * Undoing it now also puts you back where you pressed E from, because the
   * pose comes with a move into the middle of the spot: a refusal that left
   * you standing inside a full locker would be worse than the bug it fixes.
   */
  private syncHiding(me: PlayerState): void {
    if (!this.player) return;
    if (me.hidingSpotId === this.player.hidingSpotId) return;
    if (performance.now() - this.hidingPredictedAt < PREDICTION_TIMEOUT_MS) return;

    if (me.hidingSpotId >= 0) this.player.enterHiding(me.hidingSpotId);
    else this.player.leaveHiding();
  }

  /**
   * How many *other* players the server currently has inside a spot.
   *
   * Excludes self to match the server's own check, so the count means "how
   * many are in my way" rather than "how many are in there".
   */
  private spotOccupancy(spotId: number): number {
    const state = this.state;
    if (!state) return 0;

    let count = 0;
    for (const [sessionId, player] of state.players) {
      if (sessionId === this.connection.sessionId) continue;
      if (player.hidingSpotId === spotId) count++;
    }
    return count;
  }

  /** Find the nearest thing worth pressing E on. */
  private findInteractable(): Interactable | null {
    const px = this.player.x;
    const pz = this.player.z;
    const reach = PLAYER.INTERACT_RANGE;
    let best: Interactable | null = null;

    const consider = (candidate: Interactable): void => {
      if (!best || candidate.distance < best.distance) best = candidate;
    };

    // On a ladder at the top of a tower: the panel is the only option.
    if (this.player.towerId >= 0 && this.player.climbProgress > 0.9) {
      const tower = this.state?.towers[this.player.towerId];
      if (tower && !tower.activated) {
        return { kind: 'tower', id: this.player.towerId, label: 'E  BEGIN TRANSMISSION', distance: 0 };
      }
      return this.climbingHint();
    }
    if (this.player.isClimbing) {
      return this.climbingHint();
    }

    const taken = new Set(this.state?.takenItems ?? []);
    for (const item of this.world.items) {
      if (taken.has(item.id)) continue;
      const d = dist2d(px, pz, item.x, item.z);
      if (d < reach) {
        consider({ kind: 'item', id: item.id, label: `E  TAKE ${item.kind.toUpperCase()}`, distance: d });
      }
    }

    for (const site of this.world.towers) {
      const d = dist2d(px, pz, site.x, site.z);
      if (d < TOWERS.BASE_RADIUS + 2) {
        const tower = this.state?.towers[site.id];
        const label = tower?.activated ? 'E  CLIMB (ACTIVATED)' : 'E  CLIMB TOWER';
        consider({ kind: 'ladder', id: site.id, label, distance: d });
      }
    }

    /**
     * Downed teammates.
     *
     * Given a metre more reach than an item, because a collapsed body lies
     * flat across a couple of metres and you are as likely to walk up to its
     * feet as to its shoulder. Still well inside the server's own tolerance,
     * so a prompt that appears is a prompt that works.
     */
    for (const [sessionId, other] of this.state?.players ?? []) {
      if (sessionId === this.connection.sessionId) continue;
      if (other.life !== LifeState.Spectating) continue;

      const d = dist2d(px, pz, other.x, other.z);
      if (d < reach + 1) {
        consider({
          kind: 'revive',
          id: 0,
          sessionId,
          label: `E  REVIVE ${other.name.toUpperCase()}`,
          distance: d,
        });
      }
    }

    for (const spot of this.world.hidingSpots) {
      const d = dist2d(px, pz, spot.x, spot.z);
      if (d < reach) {
        const capacity = hidingCapacity(spot.kind);
        const occupants = this.spotOccupancy(spot.id);
        const full = occupants >= capacity;

        // The count is only worth showing once it tells you something: that
        // someone is already in there and whether there is still room. On an
        // empty locker "(0/1)" is just noise on the prompt you read most.
        const label = full
          ? 'FULL'
          : occupants > 0
            ? `E  HIDE  (${occupants}/${capacity})`
            : 'E  HIDE';

        consider({ kind: 'hiding', id: spot.id, label, distance: d, disabled: full });
      }
    }

    return best;
  }

  // -------------------------------------------------------------------------
  // Frame loop
  // -------------------------------------------------------------------------

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastFrame = performance.now();
    requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
  }

  private frame = (): void => {
    if (!this.running) return;
    requestAnimationFrame(this.frame);

    const now = performance.now();
    const dt = Math.min((now - this.lastFrame) / 1000, 0.1);
    this.lastFrame = now;

    if (!this.world || !this.player) return;

    // Before anything reads the local player, so the frame you are revived on
    // already has your real position — see reconcile().
    this.reconcile();
    this.updatePlayer(dt);
    // After the player has moved for this frame, whichever branch did it.
    this.updateSelfRig();
    this.updateFromState(dt, now);
    this.updateAudio(dt);
    this.updateHud(dt);

    this.forest.update(this.player.x, this.player.z);
    this.forest.tickShake(dt);
    this.props.update(dt);

    // Last, so it layers on whatever set the camera this frame — walking,
    // the forced confrontation, or the death cam.
    this.applyShake(dt);

    this.renderer.render(dt);
  };

  /**
   * Adopt anything the server has decided about us, before the frame reads it.
   *
   * Ordering is the whole point of it being here rather than inside
   * updateFromState. On the frame you are revived, syncLife moves the local
   * player from where it fell to where the server actually put it; if that
   * happened after updatePlayer, the camera — and therefore the audio
   * listener, which follows it once you are alive — would spend that frame
   * back at your body.
   */
  private reconcile(): void {
    const me = this.me;
    if (!me) return;

    this.syncLife(me);
    this.syncFlashlight(me);
    this.syncHiding(me);
  }

  private updatePlayer(dt: number): void {
    const me = this.me;
    const dead = me ? me.life !== LifeState.Alive : false;

    if (dead) {
      // Cleared, or the prompt you were standing on when he took you hangs
      // on screen through your own death sequence.
      this.currentInteractable = null;
      this.updateSpectate(dt);
      return;
    }

    // A forced confrontation overrides everything: no movement, no prompts,
    // no picking things up. Just the thing in front of you.
    if (this.player.isLookLocked) {
      this.player.update(dt, this.input, this.renderer.camera);
      this.currentInteractable = null;
      this.sendMove(dt);
      return;
    }

    // Transmitting takes over movement input: you are pinned to the panel.
    if (this.transmit.isVisible) {
      const tower = this.state?.towers[this.player.towerId];
      if (!tower || tower.activated || this.player.climbProgress < 0.85) {
        this.stopTransmitting();
      } else {
        const tune =
          (this.input.isDown('KeyD') ? 1 : 0) - (this.input.isDown('KeyA') ? 1 : 0);
        const onSignal = this.transmit.update(dt, tune, tower.signalTarget, tower.progress);
        this.connection.send(ClientMsg.TransmitTune, { dial: this.transmit.dial });

        // Audio feedback is the real interface here; the bar is confirmation.
        this.footstepAccumulator += dt;
        if (this.footstepAccumulator > 0.22) {
          this.footstepAccumulator = 0;
          if (onSignal) this.audio.carrier(1);
          else this.audio.staticBurst(0.8);
        }
      }
      // Look around freely while tuning.
      this.player.update(dt, this.input, this.renderer.camera);
      this.sendMove(dt);
      return;
    }

    this.player.update(dt, this.input, this.renderer.camera);
    this.currentInteractable = this.findInteractable();
    this.updateFootsteps(dt);
    this.updateGear(dt);
    this.sendMove(dt);
  }

  /**
   * The mid-climb "interactable" is not one — E deliberately does nothing on
   * it (re-mounting would cancel the climb). It exists as a keyboard hint for
   * the centre prompt, so on touch it must not exist at all: the interact
   * button would render keyboard instructions on a button that does nothing,
   * and the LET GO label on the context button already teaches the way down.
   */
  private climbingHint(): Interactable | null {
    if (IS_TOUCH_DEVICE) return null;
    return {
      kind: 'ladder',
      id: this.player.towerId,
      label: 'W / S  CLIMB   ·   SPACE  GET OFF',
      distance: 0,
    };
  }

  /** Passive effects of carried gear. */
  private updateGear(dt: number): void {
    // The map remembers any tower you walk near, whether or not you climb it.
    for (const site of this.world.towers) {
      if (this.discoveredTowers.has(site.id)) continue;
      if (dist2d(this.player.x, this.player.z, site.x, site.z) < GEAR.MAP_DISCOVER_RADIUS) {
        this.discoveredTowers.add(site.id);
      }
    }

    // The radio hisses, and hisses faster the closer you are to a tower that
    // still needs transmitting. Only while switched on — see toggleRadio.
    if (!this.me?.radioOn) return;

    const signal = this.radioSignalStrength();
    this.radioTimer -= dt;
    if (this.radioTimer > 0) return;

    this.radioTimer =
      GEAR.RADIO_MAX_INTERVAL - (GEAR.RADIO_MAX_INTERVAL - GEAR.RADIO_MIN_INTERVAL) * signal;

    this.audio.staticBurst(0.25 + signal * 0.75, 0.12 + (1 - signal) * 0.2);
    if (signal > GEAR.RADIO_CARRIER_THRESHOLD) {
      // Close in, the carrier starts breaking through the noise.
      this.audio.carrier((signal - GEAR.RADIO_CARRIER_THRESHOLD) * 3, 0.14);
    }
  }

  /** 0..1 proximity to the nearest tower still needing activation. */
  private radioSignalStrength(): number {
    const state = this.state;
    let nearest = Infinity;

    for (const site of this.world.towers) {
      if (state?.towers[site.id]?.activated) continue;
      nearest = Math.min(nearest, dist2d(this.player.x, this.player.z, site.x, site.z));
    }

    if (!Number.isFinite(nearest)) return 0;
    return clamp01(1 - nearest / GEAR.RADIO_RANGE);
  }

  /**
   * Everyone still on their feet, in a stable order.
   *
   * Sorted rather than left in map order so cycling is predictable: pressing
   * "next" twice must land you somewhere you can reason about, and the list
   * must not reshuffle underneath you when the server touches its state.
   */
  private spectateTargets(): string[] {
    const state = this.state;
    if (!state) return [];

    const out: string[] = [];
    for (const [sessionId, player] of state.players) {
      if (sessionId === this.connection.sessionId) continue;
      if (player.life !== LifeState.Alive) continue;
      out.push(sessionId);
    }
    return out.sort();
  }

  /** Step to the next or previous living teammate. */
  private cycleSpectate(direction: number): void {
    const targets = this.spectateTargets();
    if (targets.length === 0) return;

    const current = this.spectateTarget ? targets.indexOf(this.spectateTarget) : -1;
    const count = targets.length;
    const next = (((current + direction) % count) + count) % count;
    this.watch(targets[next]!);
  }

  /**
   * Take up a new subject, dropping the camera in behind their shoulder.
   *
   * Reframing on each switch matters: inheriting the previous subject's orbit
   * angle can put you staring at the back of someone's head or into a trunk,
   * and the first thing you want on switching is to see what they see.
   */
  private watch(sessionId: string): void {
    this.spectateTarget = sessionId;

    const target = this.state?.players.get(sessionId);
    if (!target) return;

    const forward = forwardFromYaw(target.yaw);
    this.spectateYaw = Math.atan2(-forward.x, -forward.z);
    this.spectatePitch = 0.3;
  }

  /** The player currently being watched, if any. */
  private get spectated() {
    if (this.me?.life !== LifeState.Spectating) return undefined;
    return this.spectateTarget ? this.state?.players.get(this.spectateTarget) : undefined;
  }

  private updateSpectate(dt: number): void {
    // Follow a living teammate, or drift above the body if nobody is left.
    const state = this.state;
    if (!state) return;

    const targets = this.spectateTargets();
    if (!this.spectateTarget || !targets.includes(this.spectateTarget)) {
      // Whoever we were watching has gone down. Pick up the next one rather
      // than leaving the camera pointed at a corpse.
      if (targets.length > 0) this.watch(targets[0]!);
      else this.spectateTarget = null;
    }

    /**
     * The orbit.
     *
     * Look input is drained every frame whether or not it is used, so nothing
     * accumulates during the death cinematic and snaps the view the moment
     * control of the camera arrives — the same reason the confrontation drains
     * it. It is only *applied* once you are actually spectating, because until
     * then shakeCameraForGrab owns the camera.
     */
    const look = this.input.consumeLook();
    if (this.me?.life === LifeState.Spectating) {
      this.spectateYaw += look.yaw;
      // Subtracted, not added: pushing the mouse up should raise your eyeline,
      // which swings the camera *down* around the subject.
      this.spectatePitch = clamp(
        this.spectatePitch - look.pitch,
        SPECTATE_PITCH_MIN,
        SPECTATE_PITCH_MAX,
      );
    }

    const target = this.spectateTarget ? state.players.get(this.spectateTarget) : undefined;
    const camera = this.renderer.camera;

    if (target) {
      // Spherical offset around their chest, so the orbit keeps them centred
      // however far round or above them you swing.
      const focusY = target.y + 1.4;
      const horizontal = Math.cos(this.spectatePitch) * SPECTATE_DISTANCE;
      const desired = new THREE.Vector3(
        target.x + Math.sin(this.spectateYaw) * horizontal,
        focusY + Math.sin(this.spectatePitch) * SPECTATE_DISTANCE,
        target.z + Math.cos(this.spectateYaw) * horizontal,
      );
      camera.position.lerp(desired, Math.min(1, dt * 8));
      camera.lookAt(target.x, focusY, target.z);
    } else {
      camera.position.y = damp(camera.position.y, this.player.y + 14, 1.2, dt);
      camera.lookAt(this.player.x, this.player.y, this.player.z);
    }
  }

  private updateFootsteps(dt: number): void {
    const speed = this.player.speed;
    if (speed < 0.4 || !this.player.grounded) return;

    const stance = this.player.currentStance;
    const stride = stance === Stance.Sprinting ? 0.34 : stance === Stance.Crouching ? 0.68 : 0.48;

    this.footstepAccumulator += (speed / PLAYER.WALK_SPEED) * dt;
    if (this.footstepAccumulator >= stride) {
      this.footstepAccumulator = 0;
      this.audio.footstep(stance === Stance.Crouching, stance === Stance.Sprinting);
    }
  }

  private sendMove(dt: number): void {
    const msg: MoveMsg = {
      x: this.player.x,
      y: this.player.y,
      z: this.player.z,
      yaw: this.player.yaw,
      pitch: this.player.pitch,
      stance: this.player.currentStance,
      stamina: this.player.stamina,
      t: performance.now(),
    };
    this.connection.sendMove(dt, 1 / CLIENT_SEND_RATE, msg);
  }

  // -------------------------------------------------------------------------
  // Server state -> scene
  // -------------------------------------------------------------------------

  private updateFromState(dt: number, now: number): void {
    const state = this.state;
    if (!state) return;

    const me = this.me;

    // --- Remote players -----------------------------------------------------
    const remoteStates = new Map<string, RemoteState>();
    const voicePeers: Array<{ id: string; x: number; y: number; z: number }> = [];

    /**
     * Who is in which hiding place, so their name tags can be stacked.
     *
     * Sorted by session id purely so the order is stable frame to frame —
     * names that swap heights whenever the map iterates differently would
     * flicker. Self is excluded: you never see your own tag, and leaving a
     * gap in the stack where it would have been looks like a missing player.
     */
    /**
     * Where every voice sits once the channel is open — a single point just
     * ahead of the listener, so the whole lobby arrives at full volume from
     * directly in front instead of being scattered across the forest. Null
     * while voice is still positional.
     */
    let flatVoice: { x: number; y: number; z: number } | null = null;
    if (this.openChannel) {
      const ear = this.earPosition();
      const heading = new THREE.Vector3();
      this.renderer.camera.getWorldDirection(heading);
      flatVoice = {
        x: ear.x + heading.x * OPEN_CHANNEL_DISTANCE,
        y: ear.y + heading.y * OPEN_CHANNEL_DISTANCE,
        z: ear.z + heading.z * OPEN_CHANNEL_DISTANCE,
      };
    }

    const hiddenBySpot = new Map<number, string[]>();
    for (const [sessionId, player] of state.players) {
      if (sessionId === this.connection.sessionId) continue;
      if (player.hidingSpotId < 0) continue;
      const occupants = hiddenBySpot.get(player.hidingSpotId);
      if (occupants) occupants.push(sessionId);
      else hiddenBySpot.set(player.hidingSpotId, [sessionId]);
    }
    for (const occupants of hiddenBySpot.values()) occupants.sort();

    for (const [sessionId, player] of state.players) {
      if (sessionId === this.connection.sessionId) continue;

      // Unconditional: ensure also refreshes the name tag after a lobby rename.
      this.remotes.ensure(sessionId, player.name);
      this.remotes.push(sessionId, {
        t: now,
        x: player.x,
        y: player.y,
        z: player.z,
        yaw: player.yaw,
        // Their torch is aimed from this, so it has to travel with the rest
        // of the transform rather than being dropped here.
        pitch: player.pitch,
      });
      remoteStates.set(sessionId, {
        flashlightOn: player.flashlightOn,
        life: player.life,
        stance: player.stance,
        speaking: this.voice.isSpeaking(sessionId),
        hidingSpotId: player.hidingSpotId,
        nameStackIndex: Math.max(
          0,
          hiddenBySpot.get(player.hidingSpotId)?.indexOf(sessionId) ?? 0,
        ),
      });
      // Voices come from mouths, not feet — except on the open channel, where
      // everyone is placed just in front of the listener instead.
      voicePeers.push(
        flatVoice
          ? { id: sessionId, ...flatVoice }
          : { id: sessionId, x: player.x, y: player.y + PLAYER.EYE_HEIGHT, z: player.z },
      );
    }
    this.remotes.update(dt, now, remoteStates);

    // Once the channel is open the dead are allowed to talk; until then their
    // outbound track stays disabled and they are listeners only.
    const selfAlive = me?.life === LifeState.Alive;
    this.voice.update(dt, now, voicePeers, selfAlive || this.openChannel);

    // --- Monster ------------------------------------------------------------
    const m = state.monster;
    const distance = dist2d(this.player.x, this.player.z, m.x, m.z);

    // Only render him when he could plausibly be seen. Beyond the fog he is
    // pure audio, which is where most of the fear lives anyway. A confrontation
    // overrides this — the whole point is that he is on screen.
    const couldBeVisible =
      this.confrontFocus || (distance < ATMOSPHERE.DRAW_DISTANCE && m.fsm !== Fsm.Dormant);
    this.monster.update(dt, m.x, m.y, m.z, m.yaw, m.speed, m.fsm, couldBeVisible);

    // --- Dread: proximity times awareness ------------------------------------
    const proximity = clamp01(1 - distance / 55);
    const awareness = clamp01(m.alertness / MONSTER.ALERT_MAX);
    let target = m.fsm === Fsm.Dormant ? 0 : clamp01(proximity * (0.35 + awareness * 0.65));
    if (this.confrontFocus) target = 1;
    this.dread = damp(this.dread, target, this.confrontFocus ? 9 : 2.4, dt);
    this.renderer.setDread(this.dread);

    // --- FOV pull-in, which reads as involuntary focus ------------------------
    const desiredFov = this.player.isLookLocked ? this.baseFov - CONFRONT.FOV_PUNCH : this.baseFov;
    if (Math.abs(this.renderer.camera.fov - desiredFov) > 0.05) {
      this.renderer.camera.fov = damp(this.renderer.camera.fov, desiredFov, 7, dt);
      this.renderer.camera.updateProjectionMatrix();
    }

    // --- Flares --------------------------------------------------------------
    this.syncFlares(state);

    // --- Items already collected ---------------------------------------------
    this.props.syncTakenItems(state.takenItems);

    // --- Tower lights and safe zones -----------------------------------------
    this.props.syncTowers(
      state.towers.map((tower) => ({
        id: tower.id,
        activated: tower.activated,
        transmitting: tower.transmittingBy !== '',
      })),
      state.elapsed,
    );

    // --- Extraction zone ------------------------------------------------------
    this.props.setExtractionVisible(
      state.phase === MatchPhase.RescueInbound || state.phase === MatchPhase.Extracting,
    );

    // --- Death fade -----------------------------------------------------------
    if (me && me.life === LifeState.Caught) {
      this.deathFade = Math.min(1, this.deathFade + dt / PLAYER.DEATH_SEQUENCE);
      this.renderer.setFade(this.deathFade);
      this.shakeCameraForGrab(dt, m);
    } else if (me && me.life === LifeState.Spectating) {
      this.deathFade = Math.max(0, this.deathFade - dt * 0.5);
      this.renderer.setFade(this.deathFade);
    } else {
      this.deathFade = 0;
      this.renderer.setFade(0);
    }
  }

  /**
   * The grab: hoisted off the ground and thrashed.
   *
   * The base orientation tracks his head with the shake layered on top, rather
   * than being pure noise. Same principle as the confrontation — the last
   * thing you see should be the thing that killed you, not a spinning blur.
   */
  private shakeCameraForGrab(dt: number, m: GameState['monster']): void {
    const camera = this.renderer.camera;
    const t = performance.now() / 1000;

    /**
     * Hold the camera out in front of his face, not at his position.
     *
     * The previous version damped the camera toward `m.x, m.z` — the monster's
     * own coordinates — which put the viewer *inside* the model. There was
     * nothing to look at: the direction to the target was a near-zero vector,
     * so the resulting yaw was meaningless and you saw his backfaces or empty
     * space. No amount of correcting the aim could fix a camera standing in
     * the wrong place.
     *
     * He is holding you up in front of himself, so that is where the camera
     * goes: along his facing direction, at head height, looking back at him.
     */
    const forwardX = Math.sin(m.yaw); // monster convention: forward is (sin, cos)
    const forwardZ = Math.cos(m.yaw);
    const reach = 4.6;

    const thrashX = Math.sin(t * 11) * 0.7;
    const thrashZ = Math.cos(t * 9) * 0.7;

    const holdX = m.x + forwardX * reach + thrashX;
    const holdZ = m.z + forwardZ * reach + thrashZ;
    const holdY = m.y + MONSTER.HEIGHT * 0.72 + Math.sin(t * 7) * 0.5;

    camera.position.x = damp(camera.position.x, holdX, 3.5, dt);
    camera.position.y = damp(camera.position.y, holdY, 2.2, dt);
    camera.position.z = damp(camera.position.z, holdZ, 3.5, dt);

    // Look back at his head, then add the violence on top.
    const headY = m.y + MONSTER.HEIGHT * 0.88;
    const dx = m.x - camera.position.x;
    const dz = m.z - camera.position.z;
    const horizontal = Math.max(Math.hypot(dx, dz), 0.001);

    const desiredYaw = yawToward(camera.position.x, camera.position.z, m.x, m.z);
    const desiredPitch = Math.atan2(headY - camera.position.y, horizontal);

    const blend = 1 - Math.exp(-6 * dt);
    camera.rotation.order = 'YXZ';
    camera.rotation.y += angleDelta(camera.rotation.y, desiredYaw) * blend;
    camera.rotation.x += (desiredPitch - camera.rotation.x) * blend;

    camera.rotation.z = Math.sin(t * 17) * 0.45;
    camera.rotation.x += Math.sin(t * 13) * 0.16;
    camera.rotation.y += Math.sin(t * 7) * 0.04;
  }

  private syncFlares(state: GameState): void {
    // Remove burnt-out flares.
    for (const [id, flare] of this.flareObjects) {
      if (!state.flares.has(id)) {
        this.renderer.scene.remove(flare.light);
        this.renderer.scene.remove(flare.mesh);
        this.renderer.scene.remove(flare.glow);
        this.flareObjects.delete(id);
      }
    }

    for (const [id, flare] of state.flares) {
      let obj = this.flareObjects.get(id);
      if (!obj) {
        // A light alone was easy to miss against a dark forest floor. The
        // burning body and halo are what make a thrown flare read at a glance.
        const light = new THREE.PointLight(0xff6a2a, 0, 90, 2);
        const mesh = new THREE.Mesh(
          new THREE.CylinderGeometry(0.06, 0.06, 0.34, 6),
          new THREE.MeshBasicMaterial({ color: 0xffd9a0 }),
        );
        mesh.rotation.z = Math.PI / 2.4;
        const glow = new THREE.Mesh(
          new THREE.SphereGeometry(0.9, 10, 8),
          new THREE.MeshBasicMaterial({
            color: 0xff7a3a,
            transparent: true,
            opacity: 0.32,
            depthWrite: false,
          }),
        );
        this.renderer.scene.add(light, mesh, glow);
        obj = { light, mesh, glow };
        this.flareObjects.set(id, obj);
      }

      obj.light.position.set(flare.x, flare.y + 0.5, flare.z);
      obj.mesh.position.set(flare.x, flare.y + 0.16, flare.z);
      obj.glow.position.copy(obj.light.position);

      // Flicker, and gutter out as it dies.
      const life = clamp01(flare.remaining / ITEMS.FLARE_BURN_TIME);
      const flicker = 0.82 + Math.random() * 0.18;
      obj.light.intensity = 260 * flicker * (0.35 + life * 0.65);
      obj.glow.scale.setScalar(flicker * (0.7 + life * 0.5));
      (obj.glow.material as THREE.MeshBasicMaterial).opacity = 0.34 * (0.3 + life * 0.7);
    }
  }

  // -------------------------------------------------------------------------
  // Audio and HUD
  // -------------------------------------------------------------------------

  private updateAudio(dt: number): void {
    const camera = this.renderer.camera;
    const forward = new THREE.Vector3();
    camera.getWorldDirection(forward);
    const up = camera.up;

    const me = this.me;
    const exertion = me ? clamp01(1 - me.stamina / PLAYER.STAMINA_MAX) : 0;

    this.audio.update(
      dt,
      this.earPosition(),
      { x: forward.x, y: forward.y, z: forward.z },
      { x: up.x, y: up.y, z: up.z },
      this.dread,
      exertion,
    );
  }

  /**
   * Where this client is listening from.
   *
   * Normally the camera, but while spectating it is the *subject's* head
   * rather than the orbit camera. That is what makes a downed player hear
   * exactly what the person they are watching hears: swinging the camera out
   * to one side changes your view, not your ears, so you cannot orbit away
   * from a conversation or lean toward a stomp. Orientation still comes from
   * the camera, so what you hear on your left is on your left.
   */
  private earPosition(): { x: number; y: number; z: number } {
    const target = this.spectated;
    if (target) {
      return { x: target.x, y: target.y + PLAYER.EYE_HEIGHT, z: target.z };
    }

    const position = this.renderer.camera.position;
    return { x: position.x, y: position.y, z: position.z };
  }

  /**
   * Whether voice has stopped being positional.
   *
   * Only once the run is actually over. The extraction hold is still live
   * play — the monster is at his most aggressive and being heard still costs
   * you — so opening the channel there would hand the living a free spotter
   * network at the tensest moment of the match. On the end screen none of
   * that is true any more, and the post-mortem should be a conversation
   * rather than six people who cannot hear each other. It is also the one
   * point where a downed player is allowed to transmit at all.
   */
  private get openChannel(): boolean {
    const phase = this.state?.phase;
    return phase === MatchPhase.Won || phase === MatchPhase.Lost;
  }

  private get isSpectating(): boolean {
    return this.me?.life === LifeState.Spectating;
  }

  private updateHud(dt: number): void {
    const state = this.state;
    const me = this.me;
    if (!state || !me) return;

    this.syncSelection();

    const active = this.activeItems();
    const passives = [...me.inventory].filter((kind) => isPassiveItem(kind));
    const spectating = me.life === LifeState.Spectating;
    const spectateName = this.spectateTarget
      ? (state.players.get(this.spectateTarget)?.name ?? null)
      : null;

    let prompt: string | null = null;
    if (!spectating && !this.transmit.isVisible) {
      if (me.hidingSpotId >= 0) {
        // Touch has a dedicated BREATH button, so its prompt is just the exit;
        // mentioning Space to someone with no keyboard is noise.
        prompt = IS_TOUCH_DEVICE ? 'LEAVE' : 'E  LEAVE  ·  SPACE  HOLD BREATH';
      } else if (this.currentInteractable) {
        prompt = this.currentInteractable.label;
      }
    }

    const model: HudModel = {
      // The player's own stamina, not the echo from the server: the bar and
      // the sprint gate must be the same number or they disagree visibly.
      stamina: this.player.stamina,
      exhausted: this.player.exhausted,
      battery: me.battery,
      breath: me.breath,
      hiding: me.hidingSpotId >= 0,
      health: me.health,
      heldItem: me.heldItem,
      hotbar: active,
      selectedIndex: this.selectedIndex,
      passives,
      towersActivated: state.towersActivated,
      towersRequired: state.towersRequired,
      prompt,
      rescueTimer: state.phase === MatchPhase.RescueInbound ? state.rescueTimer : null,
      extractionHold: state.phase === MatchPhase.Extracting ? state.extractionHold : null,
      spectating,
      spectatingName: spectateName,
      // Only offered when there is somewhere to switch to, and worded for the
      // device actually in the player's hands — "CLICK" means nothing on a
      // phone, where the context button reads SWITCH instead.
      spectateHint:
        spectating && this.spectateTargets().length > 1
          ? IS_TOUCH_DEVICE
            ? 'SWITCH  ·  DRAG TO LOOK'
            : 'CLICK / SPACE  SWITCH  ·  MOUSE  LOOK'
          : null,

      // Passive: once you have a map you always have it open — except on
      // touch, where it is a toggle because it shares its corner with the
      // button row.
      map:
        me.inventory.includes('map') && (!IS_TOUCH_DEVICE || this.touchMapOpen)
          ? this.buildMapModel(state)
          : null,
      // The compass is passive: carrying it is enough, since a bearing is
      // something you glance at constantly rather than stop to consult.
      compass: me.inventory.includes('compass'),
      radioSignal: me.radioOn ? this.radioSignalStrength() : null,
      radioOn: me.radioOn,
      flashlightOn: this.flashlightOn,
      voice: this.buildVoiceModel(state),
    };

    this.hud.setYaw(this.player.yaw);
    this.hud.update(dt, model);

    if (this.touch) {
      this.touch.setActive(state.phase !== MatchPhase.Lobby);
      this.touch.sync({
        torchOn: this.flashlightOn,
        radioOn: me.radioOn,
        micStatus: this.voice.status,
        micMuted: this.voice.muted,
        hiding: me.hidingSpotId >= 0,
        climbing: this.player.isClimbing,
        prompt,
        promptDisabled: this.currentInteractable?.disabled ?? false,
        spectating,
        transmitting: this.transmit.isVisible,
        hasMap: me.inventory.includes('map'),
        mapOpen: this.touchMapOpen,
      });
    }

    // Breath is released the moment the key is up.
    if (me.hidingSpotId >= 0 && !this.input.isDown('Space')) {
      this.connection.send(ClientMsg.HoldBreath, false);
    }
  }

  private buildVoiceModel(state: GameState): HudModel['voice'] {
    if (this.voice.status === 'off') return null;

    const names = new Map<string, string>();
    for (const [sessionId, player] of state.players) names.set(sessionId, player.name);

    return {
      status: this.voice.status,
      muted: this.voice.muted,
      speaking: this.voice.speaking,
      ownNoise: this.voice.ownNoise,
      speakers: this.voice.speakers(names),
    };
  }

  private buildMapModel(state: GameState): MapModel {
    const teammates: Array<{ x: number; z: number; alive: boolean }> = [];
    for (const [sessionId, other] of state.players) {
      if (sessionId === this.connection.sessionId) continue;
      teammates.push({ x: other.x, z: other.z, alive: other.life === LifeState.Alive });
    }

    const showExtraction =
      state.phase === MatchPhase.RescueInbound || state.phase === MatchPhase.Extracting;

    return {
      playerX: this.player.x,
      playerZ: this.player.z,
      playerYaw: this.player.yaw,
      towers: this.world.towers.map((site) => {
        const tower = state.towers[site.id];
        const activated = tower?.activated ?? false;
        return {
          x: site.x,
          z: site.z,
          activated,
          // Activated towers are common knowledge; the rest you must find.
          known: activated || this.discoveredTowers.has(site.id),
        };
      }),
      teammates,
      extraction: showExtraction
        ? { x: this.world.extraction.x, z: this.world.extraction.z }
        : null,
      spawn: { x: this.world.spawnPoint.x, z: this.world.spawnPoint.z },
      // Must match ForestRoom.refreshSafeZones, or the map would draw a
      // perimeter the monster does not actually respect.
      safeRadius: TOWERS.SAFE_RADIUS * profileFor(this.world.difficulty).safeRadius,
    };
  }

  // -------------------------------------------------------------------------
  // Events from the connection
  // -------------------------------------------------------------------------

  onCue(kind: number, x: number, y: number, z: number, gain: number): void {
    this.audio.playCue(kind, x, y, z, gain);

    // Footfalls and things hitting the ground are felt, not just heard.
    const isImpact =
      kind === CueKind.NearStomp || kind === CueKind.DistantStomp || kind === CueKind.TreeCrash;
    if (!isImpact || !this.player) return;

    const distance = dist2d(this.player.x, this.player.z, x, z);
    const closeness = clamp01(1 - distance / IMPACT_RANGE);
    if (closeness <= 0) return;

    // Squared so distant steps are a suggestion and close ones are an event.
    const force = closeness * closeness * gain;

    this.addTrauma(force * 0.85);
    this.forest?.shake(x, y, z, force * 0.55);
  }

  /** Screen shake, accumulated as trauma and applied as offsets each frame. */
  private addTrauma(amount: number): void {
    this.trauma = clamp01(this.trauma + amount);
  }

  /**
   * Offset the camera by the current shake.
   *
   * Applied after every other camera update, because they all set position and
   * rotation absolutely — so this can add on top without accumulating.
   */
  private applyShake(dt: number): void {
    if (this.trauma <= 0.001) {
      this.trauma = 0;
      return;
    }

    this.trauma = Math.max(0, this.trauma - dt * 1.6);

    // Trauma squared is the standard trick: it keeps small knocks subtle while
    // letting a close impact really hit.
    const intensity = this.trauma * this.trauma;
    this.shakeTime += dt;

    const camera = this.renderer.camera;
    const t = this.shakeTime;

    camera.position.x += Math.sin(t * 47) * 0.09 * intensity;
    camera.position.y += Math.sin(t * 59 + 1.7) * 0.13 * intensity;
    camera.position.z += Math.sin(t * 41 + 3.1) * 0.09 * intensity;

    camera.rotation.z += Math.sin(t * 53 + 0.6) * 0.035 * intensity;
    camera.rotation.x += Math.sin(t * 61 + 2.2) * 0.022 * intensity;
  }

  /**
   * The forced look. Control is taken, the head turns, and he announces
   * himself — so no player ever dies to something they were never shown.
   */
  onConfront(x: number, y: number, z: number, duration: number): void {
    if (!this.player) return;

    this.player.beginLookLock(x, y, z, duration);
    this.confrontFocus = true;

    // Close the transmit panel: the tower is lost, and leaving a dial on
    // screen would undercut the moment.
    this.transmit.hide();

    /**
     * Snap the torch on and let the head turn carry it onto him.
     *
     * The flashlight is parented to the camera, so aiming the camera aims the
     * beam — which means the thing that guarantees he is lit is the same thing
     * that guarantees he is on screen. Relying on lights attached to the
     * creature instead does not work: a point light at his centre leaves every
     * camera-facing surface unlit, and he reads as a black cut-out.
     */
    this.torchRestore = this.flashlightOn;
    if (!this.flashlightOn) {
      this.flashlightOn = true;
      this.flashlightPredictedAt = performance.now();
      this.connection.send(ClientMsg.ToggleFlashlight);
    }
    this.flashlight.visible = true;
    this.flashlight.intensity = ATMOSPHERE.FLASHLIGHT_INTENSITY * 1.9;
    this.flashlight.distance = ATMOSPHERE.FLASHLIGHT_RANGE * 1.6;

    // His own sirens light up too, as a rim accent behind the beam.
    this.monster.flashHorns(duration + 0.9);
    this.audio.stinger();
    this.hud.showNotice('IT SEES YOU', 2.4);

    window.setTimeout(() => {
      this.confrontFocus = false;
      this.flashlight.intensity = ATMOSPHERE.FLASHLIGHT_INTENSITY;
      this.flashlight.distance = ATMOSPHERE.FLASHLIGHT_RANGE;

      // Put the torch back how they left it — being forced to reveal yourself
      // permanently would be a harsh side effect of a cutscene.
      if (this.torchRestore === false && this.flashlightOn) {
        this.flashlightOn = false;
        this.flashlightPredictedAt = performance.now();
        this.connection.send(ClientMsg.ToggleFlashlight);
      }
      this.flashlight.visible = this.flashlightOn;
      this.torchRestore = null;
    }, (duration + 0.35) * 1000);
  }

  onCaught(sessionId: string): void {
    this.hud.flashDamage();
    if (sessionId === this.connection.sessionId) {
      this.transmit.hide();
      /**
       * Pointer lock is deliberately kept.
       *
       * Releasing it here used to hand the cursor back, which main.ts reads as
       * "unpaused -> paused" and answers with the PAUSED veil — so being
       * caught raised a pause menu over your own death. Worse, unlocked mouse
       * movement reports nothing, so the spectator orbit had no input until
       * you clicked through the veil. Escape still frees the cursor whenever
       * the player actually wants it.
       */
    }
  }

  onNotice(text: string, duration: number): void {
    this.hud.showNotice(text, duration);
  }

  onClue(text: string): void {
    this.hud.showClue(text);
    this.transmit.hide();
  }

  onCorrection(x: number, y: number, z: number): void {
    this.player?.teleport(x, y, z);
  }

  /** Player spawn position, applied once the room assigns one. */
  syncSpawn(): void {
    const me = this.me;
    if (me && this.player) this.player.teleport(me.x, me.y, me.z);
  }

  /**
   * Tear the match down without touching anything the game does not own.
   *
   * This used to clear the whole container, which also deleted the menu, the
   * HUD and the pause overlay — every sibling in `#app`. Returning to the
   * title then left the Menu holding references to detached nodes, so the
   * title screen simply never appeared.
   */
  dispose(): void {
    this.stop();
    this.input.dispose();
    this.remotes.clear();
    // Releases the microphone as well as the mesh — the browser's recording
    // indicator must not outlive the match.
    this.voice.dispose();
    this.touch?.dispose();
    this.touch = null;

    for (const [, flare] of this.flareObjects) {
      this.renderer.scene.remove(flare.light, flare.mesh, flare.glow);
    }
    this.flareObjects.clear();

    this.renderer.dispose();
  }

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  private get state(): GameState | null {
    return this.connection.state;
  }

  private get me() {
    return this.state?.players.get(this.connection.sessionId);
  }

  private isAlive(sessionId: string): boolean {
    return this.state?.players.get(sessionId)?.life === LifeState.Alive;
  }

  get debugText(): string {
    const state = this.state;
    const m = state?.monster;
    const info = this.renderer.renderer.info;
    const deg = (radians: number): string => (radians * (180 / Math.PI)).toFixed(1);
    const camera = this.renderer.camera;

    /**
     * `look` is what the player's own state says; `cam` is what the camera
     * ended the frame actually holding.
     *
     * They must agree, give or take a fraction of a degree of screen shake.
     * If pitch reads beyond about 87 degrees, or the two disagree by anything
     * like 90 or 180, the camera is being rotated by something other than the
     * mouse — which is the difference between a bad input delta and a bad
     * write further down the frame.
     */
    return (
      `pos    ${this.player.x.toFixed(0)}, ${this.player.z.toFixed(0)}\n` +
      `look   yaw ${deg(this.player.yaw)}  pitch ${deg(this.player.pitch)}\n` +
      `cam    yaw ${deg(camera.rotation.y)}  pitch ${deg(camera.rotation.x)}  roll ${deg(camera.rotation.z)}  [${camera.rotation.order}]\n` +
      `dread  ${this.dread.toFixed(2)}\n` +
      `alert  ${m?.alertness ?? 0}  fsm ${m?.fsm ?? 0}\n` +
      `calls  ${info.render.calls}  tris ${(info.render.triangles / 1000) | 0}k\n` +
      `phase  ${state?.phase ?? '-'}  players ${state?.players.size ?? 0}`
    );
  }
}
